import { FORM_INPUT_IDS } from "../constants.js";
import { formatEstimate } from '../../../../engine/core/estimate.js';

// Search keystrokes trigger a FULL shell re-render (innerHTML of the whole
// mobile UI). Until per-tab incremental rendering exists, debounce the
// render so a fast typist costs ~5 renders/sec instead of one per
// keystroke; the focus/caret-restore machinery covers the gap. The query
// itself is stored immediately, so nothing is lost if the timer is
// pre-empted by another render source.
const SEARCH_RENDER_DEBOUNCE_MS = 180;
let searchRenderTimer = null;

function scheduleSearchRender(scheduleRender, focusSelector) {
  if (searchRenderTimer) clearTimeout(searchRenderTimer);
  searchRenderTimer = setTimeout(() => {
    searchRenderTimer = null;
    scheduleRender(focusSelector);
  }, SEARCH_RENDER_DEBOUNCE_MS);
}

export function handleMobileRenameKeydown(event, { cancelInlineRename, commitInlineRename }) {
  const input = event.target.closest?.("[data-mobile-rename-input]");
  if (!input) return;
  if (event.key === "Enter") {
    event.preventDefault();
    commitInlineRename(input);
  } else if (event.key === "Escape") {
    event.preventDefault();
    cancelInlineRename();
  }
}

export function handleMobileRenameBlur(event, { commitInlineRename }) {
  const input = event.target.closest?.("[data-mobile-rename-input]");
  if (!input) return;
  commitInlineRename(input);
}

export function handleMobileInput(event, deps) {
  const {
    container,
    uiState,
    scheduleRender,
    snapshotNodeEditDraftFromDom,
    snapshotNoteEditDraftFromDom,
  } = deps;
  const target = event.target;
  if (!container?.contains(target)) return;
  if (target.id === 'mobile-add-node-estimate' && uiState.form?.type === 'add-node' && uiState.form.estimate) {
    uiState.form.estimate.value = Number(target.value);
    const label = formatEstimate(uiState.form.estimate);
    target.setAttribute('aria-valuetext', label);
    target.closest('.mobile-estimate-slider').querySelector('output').textContent = label;
    return;
  }
  if (target?.matches?.("[data-mobile-rename-input]")) {
    const ctx = uiState.inlineRenameContext;
    if (ctx && target.dataset.nodeId === ctx.nodeId) {
      uiState.inlineRenameDraft = {
        docId: ctx.docId,
        nodeId: ctx.nodeId,
        name: target.value || "",
      };
    }
    return;
  }
  if (["mobile-node-name", "mobile-node-start", "mobile-node-end", "mobile-node-notes"].includes(target?.id)) {
    snapshotNodeEditDraftFromDom();
    return;
  }
  if (["mobile-note-title", "mobile-note-content"].includes(target?.id)) {
    snapshotNoteEditDraftFromDom();
    return;
  }
  // Form sheet inputs (create/rename/add-node) — snapshot to formDraft so a
  // remote sync re-render does not reset the user's typed text. Keyed by
  // form.type so switching forms wipes the draft naturally.
  if (uiState.form && FORM_INPUT_IDS.has(target?.id)) {
    const fields = (uiState.formDraft?.type === uiState.form.type
      ? { ...uiState.formDraft.fields }
      : {});
    fields[target.id] = target.value || "";
    uiState.formDraft = { type: uiState.form.type, fields };
    return;
  }

  const actionEl = event.target.closest("[data-mobile-action]");
  if (!actionEl || !container?.contains(actionEl)) return;
  if (actionEl.dataset.mobileAction === "docs-search") {
    uiState.docsSearchQuery = actionEl.value || "";
    scheduleSearchRender(scheduleRender, "[data-mobile-action='docs-search']");
  } else if (actionEl.dataset.mobileAction === "note-search") {
    uiState.notesSearchQuery = actionEl.value || "";
    uiState.noteSearchMatch = null;
    scheduleSearchRender(scheduleRender, "[data-mobile-action='note-search']");
  } else if (actionEl.dataset.mobileAction === "global-search") {
    uiState.searchQuery = actionEl.value || "";
    scheduleSearchRender(scheduleRender, "[data-mobile-action='global-search']");
  } else if (actionEl.dataset.mobileAction === "set-member-role") {
    // <select> on a member row — fires on change; the action layer calls the
    // API and refreshes the sharing section.
    deps.shareSetMemberRole?.(actionEl.dataset.userId, actionEl.value);
  } else if (actionEl.dataset.mobileAction === "set-link-role") {
    // The role the map's link gives (docs/plans/2026-10-28-link-sharing.md).
    deps.shareLinkSetRole?.(actionEl.value);
  } else if (actionEl.dataset.mobileAction === "share-invite-input") {
    // Preserve the invite draft across shell re-renders (remote edits).
    uiState.shareInviteDraft = {
      ...(uiState.shareInviteDraft || {}),
      [actionEl.dataset.field || "name"]: actionEl.value,
    };
  } else if (actionEl.dataset.mobileAction === "set-tag-dim") {
    // Live preview via the CSS var (no re-render needed); the Store commit
    // is debounced by the caller so a drag doesn't re-render the sheet
    // (and replace the slider) on every step.
    const level = Math.max(0, Math.min(95, parseInt(actionEl.value, 10) || 0));
    document.documentElement.style.setProperty("--tag-dim-amount", String(level / 100));
    deps.commitTagDimLevel?.(level);
  }
}
